/**
 * funChallengesBattleStore.ts
 *
 * High-level FSM + UI state for the Fun Challenges feature.
 *
 * FSM transitions:
 *   idle ── sendChallenge ────────► challenging
 *   idle ── fun_room_invite ──────► incoming
 *   challenging ── fun_session_start ─► in_battle
 *   incoming    ── fun_session_start ─► in_battle
 *   in_battle   ── fun_session_end ──► ended
 *   * ── cancel/reject/expire/forfeit/error ──► idle  (after a 4s grace
 *                                              while errorMessage is shown)
 *
 * Updated by handlers in `useFunChallengesSocket` and consumed by:
 *   - the global `FunChallengesGlobalListener` (Phase 4)
 *   - the lobby route (Phase 4)
 *   - the battle screen (Phase 5)
 */

import {create} from 'zustand';

import type {
  BattleEndPayload,
  BattleScore,
  BattleSession,
  BattleStatus,
  BattleType,
  ChallengeSettings,
  FunChallengeError,
  IncomingChallengePayload,
} from '@/Screens/FunChallenges/types';

/** Last-known intent of a sent challenge so the waiting overlay can label correctly. */
export interface PendingChallenge {
  roomId?: string;
  battleType: BattleType;
  opponentId: string;
  opponentName?: string;
  settings: ChallengeSettings;
}

interface FunChallengesBattleState {
  status: BattleStatus;
  session: BattleSession | null;
  scores: BattleScore;

  /** Local index for the current player in the server-shared coords[]. */
  myIndex: number;
  /** Mirror of the opponent's coord index, derived from fun_move_broadcast.nextIndex. */
  opponentIndex: number;

  /** Squares the current user has clicked, in order. */
  myResponses: string[];
  /** Tail of the per-broadcast {correct,wrong} info — useful for animation. */
  myMoves: boolean[];
  opponentMoves: boolean[];

  pendingChallenge: PendingChallenge | null;
  incomingChallenge: IncomingChallengePayload | null;
  endResult: BattleEndPayload | null;
  errorMessage: string | null;
  /**
   * Last error code from the server. Lets the listener decide whether to
   * persist the modal or auto-clear it.
   */
  errorCode: string | null;

  // ── Phase 6 hardening flags ──────────────────────────────────────────
  /** True while a forfeit is being dispatched — guards against double-press. */
  forfeitInFlight: boolean;
  /**
   * Set by `rematchWithSettings()`. The lobby reads this on mount to
   * auto-open the settings SlideModal, then clears it.
   */
  openSettingsOnLobby: boolean;
}

interface FunChallengesBattleActions {
  // ── FSM transitions ───────────────────────────────────────────────────
  startChallenging: (pending: PendingChallenge) => void;
  setIncomingChallenge: (payload: IncomingChallengePayload) => void;
  /**
   * Called optimistically when the local user accepts an incoming challenge.
   * Clears the `incoming` modal so the user isn't stuck staring at it while
   * we wait for the server's `fun_session_start`. Status drops to `idle`;
   * the eventual `fun_session_start` will transition us to `in_battle`.
   */
  markChallengeAccepted: () => void;
  /**
   * Dismiss an incoming challenge without starting a new session.
   * If the user was viewing a prior battle's result modal when the invite
   * arrived (so `setIncomingChallenge` preserved `endResult`), restore
   * status to `'ended'` so the result modal stays visible. Otherwise
   * fall through to `idle`. Called by `rejectChallenge` / auto-decline.
   */
  dismissIncomingChallenge: () => void;
  startInBattle: (session: BattleSession) => void;
  endBattle: (result: BattleEndPayload) => void;
  reset: () => void;

  // ── In-battle updates ─────────────────────────────────────────────────
  recordMyMove: (correct: boolean, square: string, nextIndex?: number) => void;
  recordOpponentMove: (
    playerId: string,
    correct: number,
    wrong: number,
    nextIndex: number,
  ) => void;

  // ── Errors ────────────────────────────────────────────────────────────
  setError: (err: FunChallengeError | string) => void;
  clearError: () => void;

  // ── Bulk hydration (used by fun_session_rejoin) ───────────────────────
  hydrateSession: (
    session: BattleSession,
    scores: BattleScore,
    myResponses: string[],
    /**
     * Reconstructed traffic-light histories. Optional so older callers
     * (and back-compat with servers that don't ship `allResponses`)
     * still work — those land with empty arrays and the rows simply
     * stay blank until new broadcasts arrive.
     */
    moveHistory?: {myMoves: boolean[]; opponentMoves: boolean[]},
  ) => void;

  // ── Phase 6 hardening ─────────────────────────────────────────────────
  setForfeitInFlight: (inFlight: boolean) => void;
  requestOpenSettingsOnLobby: () => void;
  clearOpenSettingsOnLobby: () => void;
}

const initialScores: BattleScore = {
  myCorrect: 0,
  myWrong: 0,
  opponentCorrect: 0,
  opponentWrong: 0,
};

const initialState: FunChallengesBattleState = {
  status: 'idle',
  session: null,
  scores: {...initialScores},
  myIndex: 0,
  opponentIndex: 0,
  myResponses: [],
  myMoves: [],
  opponentMoves: [],
  pendingChallenge: null,
  incomingChallenge: null,
  endResult: null,
  errorMessage: null,
  errorCode: null,
  forfeitInFlight: false,
  openSettingsOnLobby: false,
};

const useFunChallengesBattleStore = create<
  FunChallengesBattleState & FunChallengesBattleActions
>(set => ({
  ...initialState,

  startChallenging: pending =>
    set({
      ...initialState,
      status: 'challenging',
      pendingChallenge: pending,
    }),

  setIncomingChallenge: payload =>
    set(prev => {
      // If the invite arrives while the user is still on the result modal
      // of a just-ended battle, preserve `session`/`endResult`/`scores` so
      // BattleScreen can keep rendering the result behind the global
      // accept/deny modal. Without this the store wipe would blank out the
      // result and leave only the modal floating over an empty page.
      const onResultModal = prev.status === 'ended' && !!prev.endResult;
      if (onResultModal) {
        return {
          ...initialState,
          status: 'incoming',
          incomingChallenge: payload,
          session: prev.session,
          endResult: prev.endResult,
          scores: prev.scores,
          myIndex: prev.myIndex,
          opponentIndex: prev.opponentIndex,
          myResponses: prev.myResponses,
          myMoves: prev.myMoves,
          opponentMoves: prev.opponentMoves,
        };
      }
      return {
        ...initialState,
        status: 'incoming',
        incomingChallenge: payload,
      };
    }),

  markChallengeAccepted: () =>
    set({
      ...initialState,
    }),

  dismissIncomingChallenge: () =>
    set(prev => {
      // Preserved-result case: we were on the result modal when the invite
      // arrived and `setIncomingChallenge` kept session/endResult around.
      // Restore status to 'ended' so the result modal stays put — no idle
      // "Reconnecting…" flash, no 3.5s lobby bounce.
      if (prev.endResult && prev.session) {
        return {
          ...prev,
          status: 'ended',
          incomingChallenge: null,
        };
      }
      return {...initialState};
    }),

  startInBattle: session =>
    set(prev => ({
      ...initialState,
      status: 'in_battle',
      session,
      // Carry forward errorMessage clear; everything else fresh.
      pendingChallenge: prev.pendingChallenge,
    })),

  endBattle: result =>
    set(prev => {
      const myUserId = prev.session?.myUserId;
      const opponentId = prev.session?.opponentId;

      const myFinal = myUserId ? result.scores[myUserId] : undefined;
      const oppFinal = opponentId ? result.scores[opponentId] : undefined;

      return {
        ...prev,
        status: 'ended',
        endResult: result,
        forfeitInFlight: false, // server confirmed end; clear the dedupe guard
        scores: {
          myCorrect: myFinal?.correct ?? prev.scores.myCorrect,
          myWrong:   myFinal?.wrong   ?? prev.scores.myWrong,
          opponentCorrect: oppFinal?.correct ?? prev.scores.opponentCorrect,
          opponentWrong:   oppFinal?.wrong   ?? prev.scores.opponentWrong,
        },
      };
    }),

  reset: () => set({...initialState}),

  recordMyMove: (correct, square, nextIndex) =>
    set(prev => ({
      myResponses: [...prev.myResponses, square],
      myMoves: [...prev.myMoves, correct],
      myIndex:
        typeof nextIndex === 'number' ? nextIndex : prev.myIndex + 1,
      scores: {
        ...prev.scores,
        myCorrect: prev.scores.myCorrect + (correct ? 1 : 0),
        myWrong:   prev.scores.myWrong   + (correct ? 0 : 1),
      },
    })),

  recordOpponentMove: (playerId, correct, wrong, nextIndex) =>
    set(prev => {
      // The server echoes this broadcast back to the sender too — so this
      // action runs for BOTH players, even though it's nominally "opponent".
      // We branch off myUserId to update the right side.
      const myUserId = prev.session?.myUserId;
      if (myUserId && String(playerId) === String(myUserId)) {
        return {
          myIndex: nextIndex,
          scores: {
            ...prev.scores,
            myCorrect: correct,
            myWrong:   wrong,
          },
        };
      }
      // Idempotency: nextIndex is the server-authoritative count of moves
      // the opponent has made so far. If we already have that many dots,
      // this broadcast is a duplicate (replay, double-register bug, rejoin
      // overlap, etc.) and must NOT append again — otherwise the rendered
      // row drifts out of sync with the actual score.
      if (prev.opponentMoves.length >= nextIndex) {
        return {
          opponentIndex: nextIndex,
          scores: {
            ...prev.scores,
            opponentCorrect: correct,
            opponentWrong:   wrong,
          },
        };
      }
      const lastOppCorrect = prev.scores.opponentCorrect;
      const moveWasCorrect = correct > lastOppCorrect;
      return {
        opponentIndex: nextIndex,
        opponentMoves: [...prev.opponentMoves, moveWasCorrect],
        scores: {
          ...prev.scores,
          opponentCorrect: correct,
          opponentWrong:   wrong,
        },
      };
    }),

  setError: err => {
    const message =
      typeof err === 'string'
        ? err
        : err?.message ||
          (err?.code === 'INVITE_EXPIRED'
            ? 'Challenge expired. No response received.'
            : err?.code === 'PLAYER_BUSY'
              ? 'Opponent is already in a game.'
              : err?.code === 'ROOM_CLOSED'
                ? 'Challenge was rejected or cancelled.'
                : 'Something went wrong.');
    const code = typeof err === 'string' ? null : err?.code ?? null;
    // Lobby/room-scope errors should never tear down an active battle.
    // The server's invite-timer can fire for a stale (orphan) room AFTER a
    // new session has already started — earlier this kicked the user out
    // of the live game. Sessions can only end via `fun_session_end` or
    // session-scoped error codes.
    const isLobbyError =
      code === 'INVITE_EXPIRED' ||
      code === 'ROOM_CLOSED' ||
      code === 'ROOM_NOT_FOUND' ||
      code === 'ROOM_NOT_WAITING' ||
      code === 'NOT_CREATOR' ||
      code === 'NOT_ENOUGH_PLAYERS' ||
      code === 'INVALID_PLAYER_COUNT' ||
      code === 'PLAYER_BUSY';
    set(prev => {
      if (isLobbyError && prev.status === 'in_battle') {
        // Drop it on the floor — log only.
        return prev;
      }
      return {
        ...prev,
        status: 'idle',
        errorMessage: message,
        errorCode: code,
      };
    });
  },

  clearError: () => set({errorMessage: null, errorCode: null}),

  hydrateSession: (session, scores, myResponses, moveHistory) =>
    set({
      ...initialState,
      status: 'in_battle',
      session,
      scores,
      myResponses,
      // If the server shipped `allResponses` we hand the caller the
      // already-reconstructed boolean trails; otherwise leave them empty
      // and the rows will refill from incoming `fun_move_broadcast`s.
      myMoves: moveHistory?.myMoves ?? [],
      opponentMoves: moveHistory?.opponentMoves ?? [],
      myIndex: scores.myCorrect + scores.myWrong,
      opponentIndex:
        scores.opponentCorrect + scores.opponentWrong,
    }),

  setForfeitInFlight: inFlight => set({forfeitInFlight: inFlight}),
  requestOpenSettingsOnLobby: () => set({openSettingsOnLobby: true}),
  clearOpenSettingsOnLobby: () => set({openSettingsOnLobby: false}),
}));

export default useFunChallengesBattleStore;
